/** Detección de categorías de GRUPO ÚNICO (round robin, group_count == 1).
 *
 * Pregunta de Eduardo (2026-07-22): si la categoría solo tiene 1 grupo, el
 * paso Grupos sobra. Con este predicado el paso auto-asigna a todos vía
 * POST /groups/auto-assign-single. La resolución de formato refleja la del
 * backend (compute_setup_status): la división manda si tiene formato propio;
 * el bucket sin categoría usa el config del torneo.
 */

import type { TournamentDivision, TournamentFormatConfig } from "./api";

interface GroupStageLike {
  group_count?: unknown;
}

function groupCountOf(config: unknown): number | null {
  if (!config || typeof config !== "object") return null;
  const stage = (config as { group_stage?: GroupStageLike }).group_stage;
  if (!stage || typeof stage !== "object") return null;
  const count = stage.group_count;
  return typeof count === "number" ? count : null;
}

export function isSingleGroupRoundRobinScope(
  division: Pick<TournamentDivision, "format_type" | "format_config_json"> | null,
  formatConfig: Pick<TournamentFormatConfig, "format_type" | "config_json"> | null,
  tournamentType: string | null | undefined,
): boolean {
  if (division?.format_type) {
    return (
      division.format_type === "round_robin" &&
      groupCountOf(division.format_config_json) === 1
    );
  }
  const effectiveType = formatConfig?.format_type ?? tournamentType ?? null;
  return (
    effectiveType === "round_robin" &&
    groupCountOf(formatConfig?.config_json ?? null) === 1
  );
}

/** Formato efectivo del scope (la división manda si tiene formato propio). */
function effectiveFormatType(
  division: Pick<TournamentDivision, "format_type" | "format_config_json"> | null,
  formatConfig: Pick<TournamentFormatConfig, "format_type" | "config_json"> | null,
  tournamentType: string | null | undefined,
): string | null {
  return division?.format_type ?? formatConfig?.format_type ?? tournamentType ?? null;
}

/** ¿El paso Grupos se puede resolver solo? (Cowork 2026-09-20)
 *
 * - Round robin: si el Formato dice 1 grupo.
 * - Eliminatoria: siempre (un cuadro es un solo grupo).
 * - Escalera: mientras el admin no haya armado 2+ grupos.
 * Espejo de groups/services.auto_assign_single_group. */
export function isSingleGroupScope(
  division: Pick<TournamentDivision, "format_type" | "format_config_json"> | null,
  formatConfig: Pick<TournamentFormatConfig, "format_type" | "config_json"> | null,
  tournamentType: string | null | undefined,
  activeGroupsInScope: number,
): boolean {
  if (activeGroupsInScope > 1) return false;
  const type = effectiveFormatType(division, formatConfig, tournamentType);
  if (type === "elimination" || type === "ladder") return true;
  return isSingleGroupRoundRobinScope(division, formatConfig, tournamentType);
}


/** «Número de grupos» que pide el Formato del scope (o null si no lo dice). */
export function formatGroupCount(
  division: Pick<TournamentDivision, "format_type" | "format_config_json"> | null,
  formatConfig: Pick<TournamentFormatConfig, "format_type" | "config_json"> | null,
): number | null {
  return division?.format_type ? groupCountOf(division.format_config_json) : groupCountOf(formatConfig?.config_json ?? null);
}
